// Radha — legal/recovery escalation: the step after dunning exhausts itself.
// Candidates are accounts still unpaid at high risk/DPD with no open case;
// escalating opens a legal_cases row of the chosen type. "Generate notice"
// drafts letter text from real account data — reviewed by an advocate and
// issued on their letterhead before it's a real legal notice, not sent by Radha.
import React, { useState } from "react";

import {
  useLegalCases, useOpenLegalCase, useUpdateLegalCase, useStatutoryInterest, useLegalCaseTypes, legalErrorMessage,
  type LegalCase, type LegalCaseType, type LegalCaseStatus,
} from "../hooks/useLegalCases";
import { legalNoticeText } from "../lib/legalNotice";
import type { CollectionAccount } from "../types/collections";
import { fmtMoney as inr, fmtDate } from "../lib/money";


// Fallback labels for rendering EXISTING cases; the picker itself is driven
// by legal_case_types (069), not by this map.
const CASE_LABEL: Record<string, string> = {
  legal_notice: "Legal notice", cheque_bounce_138: "Cheque bounce (§138 NI Act)",
  recovery_agency: "Recovery agency", nclt_insolvency: "NCLT / insolvency",
  msme_samadhaan: "MSME Samādhān (§16 MSMED Act)",
};
const STATUS_COLOR: Record<LegalCaseStatus, string> = {
  open: "var(--status-warning)", resolved: "var(--status-good)", withdrawn: "var(--fg-muted)",
};

function EscalateButton({ account, tenantName, tenantGstin, tenantCountry }: {
  account: CollectionAccount; tenantName: string; tenantGstin: string | null; tenantCountry: string;
}) {
  const open = useOpenLegalCase();
  const { data: caseTypes } = useLegalCaseTypes(tenantCountry);
  const [caseType, setCaseType] = useState<LegalCaseType>("legal_notice");
  const [note, setNote] = useState<string | null>(null);
  const [showNotice, setShowNotice] = useState(false);
  const [udyamNumber, setUdyamNumber] = useState("");

  const principal = Number(account.outstanding_amount);
  // Accrual starts the day the debt fell due, which is dpd days ago — the
  // reference rate is then read as at THAT date, not today's (068).
  const accrualFrom = new Date(Date.now() - account.days_past_due * 86_400_000).toISOString().slice(0, 10);
  const { data: si } = useStatutoryInterest(tenantCountry, principal, accrualFrom);
  // Pack routes, minus Samādhān unless the SUPPLIER is a registered MSME (062).
  const availableTypes = (caseTypes ?? []).filter((t) => t.case_type !== "msme_samadhaan" || account.is_msme);

  // If the tenant's jurisdiction changed, the previously-selected route may no
  // longer exist in the pack — fall back to its first entry.
  const effectiveType = availableTypes.some((t) => t.case_type === caseType)
    ? caseType : (availableTypes[0]?.case_type ?? caseType);

  const go = () => {
    setNote(null);
    const extra = effectiveType === "msme_samadhaan"
      ? {
          udyam_number: udyamNumber || null,
          bank_rate_annual: si?.reference_rate ? Number(si.reference_rate) : null,
          statutory_rate_annual: si?.applied_rate ? Number(si.applied_rate) : null,
          interest_as_of: accrualFrom,
          statutory_interest_accrued: si?.interest ? Number(si.interest) : null,
        }
      : {};
    open.mutate({ account_id: account.id, case_type: effectiveType, amount_involved: principal, ...extra }, {
      onSuccess: () => setNote("Escalated"),
      onError: (e) => setNote(legalErrorMessage(e)),
    });
  };

  const notice = legalNoticeText({
    tenantLegalName: tenantName, tenantGstin, debtorName: account.debtor_name,
    outstanding: principal, daysPastDue: account.days_past_due,
  });

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <select value={effectiveType} onChange={(e) => setCaseType(e.target.value as LegalCaseType)}
        style={{ padding: "2px 6px", fontSize: 12, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }}>
        {availableTypes.map((t) => <option key={t.case_type} value={t.case_type}>{t.label}</option>)}
      </select>
      {effectiveType === "msme_samadhaan" && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
          <input value={udyamNumber} onChange={(e) => setUdyamNumber(e.target.value)} placeholder="Udyam number"
            style={{ width: 130, padding: "2px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
          {si && si.is_statutory ? (
            <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
              {si.reference_rate}% ref → {si.applied_rate}% {si.compounding} over {si.days}d ·{" "}
              <strong style={{ color: "var(--fg)" }}>{inr(si.interest)}</strong>
              {Number(si.fixed_compensation) > 0 && ` + ${si.fixed_compensation} ${si.compensation_currency} fixed`}
              {si.reference_source && (
                <span style={{ color: "var(--fg-faint)" }}>
                  {" "}· rate as of {si.reference_as_of} ({si.reference_source}) — verify before filing
                </span>
              )}
            </span>
          ) : si ? (
            <span style={{ fontSize: 11, color: "var(--status-warning)" }}>{si.regime_name} — no statutory interest to claim</span>
          ) : (
            <span style={{ fontSize: 11, color: "var(--fg-faint)" }}>computing…</span>
          )}
        </span>
      )}
      <button onClick={go} disabled={open.isPending}
        style={{ padding: "3px 10px", fontSize: 12, border: 0, borderRadius: 6, background: "var(--status-critical)", color: "#fff", cursor: "pointer" }}>
        {open.isPending ? "…" : "Escalate"}
      </button>
      <button onClick={() => setShowNotice((v) => !v)}
        style={{ padding: "3px 8px", fontSize: 11.5, border: "1px solid var(--border)", borderRadius: 6, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
        {showNotice ? "Hide notice" : "Draft notice"}
      </button>
      {note && <span style={{ fontSize: 11.5, color: note === "Escalated" ? "var(--status-good)" : "var(--status-critical)" }}>{note}</span>}
      {showNotice && (
        <div style={{ flexBasis: "100%", marginTop: 6 }}>
          <textarea readOnly value={notice} rows={10}
            style={{ width: "100%", maxWidth: 560, fontSize: 12, fontFamily: "'DM Mono', ui-monospace, monospace", padding: 10, border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface-2)", color: "var(--fg)" }} />
          <div>
            <button onClick={() => navigator.clipboard.writeText(notice)}
              style={{ marginTop: 4, padding: "3px 10px", fontSize: 11.5, border: "1px solid var(--border)", borderRadius: 6, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
              Copy text
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

function MsefcReferenceInline({ c, canWrite }: { c: LegalCase; canWrite: boolean }) {
  const update = useUpdateLegalCase();
  const [ref, setRef] = useState(c.msefc_reference ?? "");
  const [note, setNote] = useState<string | null>(null);
  if (!canWrite) return c.msefc_reference ? <span> · MSEFC ref {c.msefc_reference}</span> : null;
  const save = () => {
    setNote(null);
    update.mutate({ id: c.id, msefc_reference: ref || null }, {
      onSuccess: () => setNote("Saved"),
      onError: (e) => setNote(legalErrorMessage(e)),
    });
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 6 }}>
      <input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="MSEFC reference (once filed)"
        style={{ width: 150, padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
      <button onClick={save} disabled={update.isPending}
        style={{ padding: "1px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
        save
      </button>
      {note && <span style={{ fontSize: 10.5, color: "var(--status-good)" }}>{note}</span>}
    </span>
  );
}

function CaseRow({ c, debtorName, canWrite }: { c: LegalCase; debtorName: string; canWrite: boolean }) {
  const update = useUpdateLegalCase();
  return (
    <div style={{ borderTop: "1px solid var(--border)", padding: "8px 0", fontSize: 12.5 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong>{debtorName}</strong>
        <span style={{ color: "var(--fg-muted)" }}>{CASE_LABEL[c.case_type] ?? c.case_type}</span>
        <span style={{ color: "var(--fg-muted)" }}>{inr(Number(c.amount_involved))}</span>
        {canWrite ? (
          <select value={c.status} onChange={(e) => update.mutate({ id: c.id, status: e.target.value, resolved_at: e.target.value !== "open" ? new Date().toISOString() : null })}
            style={{ padding: "1px 6px", fontSize: 11.5, border: `1px solid ${STATUS_COLOR[c.status]}`, color: STATUS_COLOR[c.status], background: "none", borderRadius: 5 }}>
            <option value="open">open</option><option value="resolved">resolved</option><option value="withdrawn">withdrawn</option>
          </select>
        ) : <span style={{ color: STATUS_COLOR[c.status] }}>{c.status}</span>}
        <span style={{ color: "var(--fg-faint)", fontSize: 11 }}>opened {fmtDate(c.opened_at)}</span>
      </div>
      {c.case_type === "cheque_bounce_138" && (c.court_name || c.next_hearing_date) && (
        <div style={{ color: "var(--fg-muted)", marginTop: 2 }}>{c.court_name ?? "—"}{c.next_hearing_date ? ` · next hearing ${c.next_hearing_date}` : ""}</div>
      )}
      {c.case_type === "recovery_agency" && (c.agency_name || c.commission_pct) && (
        <div style={{ color: "var(--fg-muted)", marginTop: 2 }}>{c.agency_name ?? "—"}{c.commission_pct ? ` · ${c.commission_pct}% commission` : ""}{c.amount_recovered ? ` · ${inr(Number(c.amount_recovered))} recovered` : ""}</div>
      )}
      {c.case_type === "nclt_insolvency" && (c.nclt_bench || c.nclt_case_number) && (
        <div style={{ color: "var(--fg-muted)", marginTop: 2 }}>{c.nclt_bench ?? "—"}{c.nclt_case_number ? ` · case ${c.nclt_case_number}` : ""}</div>
      )}
      {c.case_type === "msme_samadhaan" && (
        <div style={{ color: "var(--fg-muted)", marginTop: 2, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          {c.udyam_number ? `Udyam ${c.udyam_number}` : "Udyam —"}
          {c.statutory_rate_annual ? ` · §16 interest @ ${Number(c.statutory_rate_annual).toFixed(2)}%` : ""}
          {c.statutory_interest_accrued ? ` = ${inr(Number(c.statutory_interest_accrued))} as of ${c.interest_as_of ?? "—"}` : ""}
          <MsefcReferenceInline c={c} canWrite={canWrite} />
        </div>
      )}
    </div>
  );
}

export function LegalEscalation({ accounts, tenantName, tenantGstin, tenantIsMsme, tenantCountry, canWrite }: {
  accounts: CollectionAccount[]; tenantName: string; tenantGstin: string | null;
  tenantIsMsme: boolean; tenantCountry: string; canWrite: boolean;
}) {
  const { data: cases } = useLegalCases();
  const byAccount = new Map((accounts ?? []).map((a) => [a.id, a]));
  const openAccountIds = new Set((cases ?? []).filter((c) => c.status === "open").map((c) => c.account_id));

  // candidates: severe/critical stage or dpd > 90 (the general legal-recourse
  // bar), OR — when THIS tenant (the supplier) is MSME-registered (062) —
  // past the §15 45-day statutory period, which makes every account
  // Samadhaan-eligible (001) well before the other escalation paths would
  // kick in, so it needs its own lower threshold rather than waiting for 90+.
  const candidates = (accounts ?? []).filter(
    (a) => !openAccountIds.has(a.id)
      && (a.stage === "severe" || a.stage === "critical" || a.days_past_due > 90 || a.samadhaan_eligible),
  );

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginTop: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Legal &amp; recovery escalation</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 12px" }}>
        For accounts where dunning has run its course. A drafted legal notice needs your advocate's review and
        letterhead before it's real — Radha tracks the case, not the courtroom.
        {tenantIsMsme && (
          <> {tenantName} is MSME-registered, so under §15 of the MSMED Act no customer's agreed payment period can
          exceed 45 days — every account overdue past that is Samadhaan-eligible for §16 compound interest, regardless
          of the customer's own size.</>
        )}
      </p>

      {canWrite && candidates.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ font: "600 12.5px Inter", marginBottom: 6, color: "var(--status-critical)" }}>Candidates ({candidates.length})</div>
          <div style={{ display: "grid", gap: 8 }}>
            {candidates.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5 }}>
                <span style={{ minWidth: 140 }}>{a.debtor_name}</span>
                <span style={{ color: "var(--fg-muted)" }}>{inr(Number(a.outstanding_amount))} · {a.days_past_due}d</span>
                <EscalateButton account={a} tenantName={tenantName} tenantGstin={tenantGstin} tenantCountry={tenantCountry} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ font: "600 12.5px Inter", margin: "10px 0 4px" }}>Cases ({(cases ?? []).length})</div>
      {(cases ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-muted)", fontSize: 13 }}>No legal cases yet.</p>
      ) : (
        <div>
          {(cases ?? []).map((c) => (
            <CaseRow key={c.id} c={c} debtorName={byAccount.get(c.account_id)?.debtor_name ?? "Unknown"} canWrite={canWrite} />
          ))}
        </div>
      )}
    </div>
  );
}
